/**
 * Gold Price Card JavaScript
 * Handles dynamic price updates and user interactions
 */

class GoldPriceCard {
  constructor(apiUrl) {
    this.apiUrl = apiUrl || 'https://almas-gold-price-fix.vercel.app/api/gold-price';
    this.refreshInterval = null;
    this.isLoading = false;
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.loadInitialData();
    this.startAutoRefresh();
  }

  bindEvents() {
    // Refresh button click
    const refreshBtn = document.querySelector('.refresh-btn');
    if (refreshBtn) {
      refreshBtn.addEventListener('click', () => this.refreshPrices());
    }

    // Auto-refresh on visibility change
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.refreshPrices();
      }
    });
  }

  async loadInitialData() {
    await this.fetchGoldPrices();
  }

  async fetchGoldPrices() {
    if (this.isLoading) return;
    
    this.isLoading = true;
    this.showLoading();

    try {
      // Add '/current' to the API URL to match the server endpoint
      const response = await fetch(`${this.apiUrl}/current`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      this.updatePriceDisplay(data);
      this.showSuccess();
      
    } catch (error) {
      console.error('Error fetching gold prices:', error);
      this.showError();
    } finally {
      this.isLoading = false;
      this.hideLoading();
    }
  }

  updatePriceDisplay(data) {
    // Map the API response to the expected format
    const goldPrice = data.goldPrice || data.rates?.pkrPerGram;
    const currentUsdRate = data.usdRate;
    const usdPerGram = data.rates?.usdPerGram;
    const usdPerOunce = data.rates?.usdPerOunce;
    const pkrPerTola = data.rates?.pkrPerTola;
    const timestamp = data.timestamp;

    // Update individual rate values
    this.updateRateValue('pkr-gram', goldPrice, 'PKR');
    this.updateRateValue('usd-rate', currentUsdRate, 'PKR');
    this.updateRateValue('usd-gram', usdPerGram, 'USD');
    this.updateRateValue('usd-ounce', usdPerOunce, 'USD');
    this.updateRateValue('pkr-tola', pkrPerTola, 'PKR');

    // Update timestamp
    this.updateTimestamp(timestamp);

    // Add animation to updated values
    this.animateUpdatedValues();
  }

  updateRateValue(rateType, value, currency) {
    const rateElement = document.querySelector(`[data-rate="${rateType}"]`);
    if (!rateElement) return;

    const valueElement = rateElement.querySelector('.rate-value');
    const changeElement = rateElement.querySelector('.rate-change');
    
    if (valueElement) {
      const oldValue = parseFloat(valueElement.textContent.replace(/[^\d.-]/g, ''));
      const newValue = parseFloat(value);
      
      // Format the new value
      const formattedValue = this.formatCurrency(newValue, currency);
      valueElement.textContent = formattedValue;
      
      // Show change indicator if we have a previous value
      if (!isNaN(oldValue) && changeElement) {
        const change = newValue - oldValue;
        const changePercent = ((change / oldValue) * 100).toFixed(2);
        
        if (Math.abs(change) > 0.01) {
          changeElement.textContent = `${change > 0 ? '+' : ''}${changePercent}%`;
          changeElement.className = `rate-change ${change > 0 ? 'positive' : 'negative'}`;
          changeElement.style.display = 'inline-block';
        } else {
          changeElement.style.display = 'none';
        }
      }
    }
  }

  formatCurrency(value, currency) {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'N/A';
    
    if (currency === 'USD') {
      return `$${numValue.toFixed(2)}`;
    } else if (currency === 'PKR') {
      return `₨${numValue.toLocaleString('en-PK', { maximumFractionDigits: 0 })}`;
    }
    
    return numValue.toFixed(2);
  }

  updateTimestamp(timestamp) {
    const timestampElement = document.querySelector('.last-updated');
    if (timestampElement && timestamp) {
      const date = new Date(timestamp);
      const formattedTime = date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      timestampElement.textContent = `Last updated: ${formattedTime}`;
    }
  }

  animateUpdatedValues() {
    const rateItems = document.querySelectorAll('.rate-item');
    rateItems.forEach((item, index) => {
      setTimeout(() => {
        item.style.transform = 'scale(1.02)';
        setTimeout(() => {
          item.style.transform = 'scale(1)';
        }, 200);
      }, index * 100);
    });
  }

  async refreshPrices() {
    const refreshBtn = document.querySelector('.refresh-btn');
    if (refreshBtn) {
      refreshBtn.classList.add('spinning');
    }

    await this.fetchGoldPrices();

    if (refreshBtn) {
      setTimeout(() => {
        refreshBtn.classList.remove('spinning');
      }, 1000);
    }
  }

  showLoading() {
    const card = document.querySelector('.gold-price-card');
    if (card) {
      card.classList.add('loading');
    }
  }

  hideLoading() {
    const card = document.querySelector('.gold-price-card');
    if (card) {
      card.classList.remove('loading');
    }
  }

  showError() {
    const card = document.querySelector('.gold-price-card');
    if (card) {
      card.classList.add('error');
      
      // Show error message in rate values
      const rateValues = document.querySelectorAll('.rate-value');
      rateValues.forEach(element => {
        if (!element.textContent || element.textContent === 'Loading...') {
          element.textContent = 'Error loading';
        }
      });
    }

    // Show toast notification
    this.showToast('');
  }

  showSuccess() {
    const card = document.querySelector('.gold-price-card');
    if (card) {
      card.classList.remove('error');
    }
  }

  showToast(message, type = 'info') {
    // Create toast element
    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    toast.textContent = message;
    
    // Style the toast
    Object.assign(toast.style, {
      position: 'fixed',
      top: '20px',
      right: '20px',
      padding: '12px 20px',
      borderRadius: '8px',
      color: 'white',
      fontWeight: '500',
      zIndex: '10000',
      transform: 'translateX(100%)',
      transition: 'transform 0.3s ease',
      backgroundColor: type === 'error' ? '#e74c3c' : '#27ae60'
    });

    document.body.appendChild(toast);

    // Animate in
    setTimeout(() => {
      toast.style.transform = 'translateX(0)';
    }, 100);

    // Remove after 3 seconds
    setTimeout(() => {
      toast.style.transform = 'translateX(100%)';
      setTimeout(() => {
        document.body.removeChild(toast);
      }, 300);
    }, 3000);
  }

  startAutoRefresh() {
    // Refresh every 5 minutes
    this.refreshInterval = setInterval(() => {
      if (!document.hidden) {
        this.fetchGoldPrices();
      }
    }, 5 * 60 * 1000);
  }

  stopAutoRefresh() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  destroy() {
    this.stopAutoRefresh();
    
    // Remove event listeners
    const refreshBtn = document.querySelector('.refresh-btn');
    if (refreshBtn) {
      refreshBtn.removeEventListener('click', this.refreshPrices);
    }
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  // Get API URL from section settings or use default
  const goldPriceSection = document.querySelector('.gold-price-section');
  const apiUrl = goldPriceSection?.dataset.apiUrl;
  
  // Initialize the gold price card
  window.goldPriceCard = new GoldPriceCard(apiUrl);
});

// Cleanup on page unload
window.addEventListener('beforeunload', function() {
  if (window.goldPriceCard) {
    window.goldPriceCard.destroy();
  }
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = GoldPriceCard;
}